import React, { useState } from 'react';
import './Welcome.css';

interface WelcomeProps {
  onStartDocument: (prompt: string, useDeepResearch: boolean) => void;
  isLoading: boolean;
}

const Welcome: React.FC<WelcomeProps> = ({ onStartDocument, isLoading }) => {
  const [prompt, setPrompt] = useState('');
  const [useDeepResearch, setUseDeepResearch] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (prompt.trim() && !isLoading) {
      onStartDocument(prompt.trim(), useDeepResearch);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e as any);
    }
  };

  const examplePrompts = [
    "Create a comprehensive AWS migration strategy and implementation plan for a legacy on-premise banking system to AWS cloud",
    "Develop a technical specification document for a modern microservices architecture using AWS ECS and API Gateway",
    "Generate a business proposal for implementing AI/ML-powered customer analytics using AWS SageMaker and Redshift",
    "Create a disaster recovery and business continuity plan for a multi-region AWS infrastructure",
    "Write a technical architecture document for a serverless e-commerce platform using AWS Lambda and DynamoDB",
    "Generate a cost optimization and resource management strategy for a large-scale AWS environment",
    "Create a security and compliance framework for a healthcare application on AWS with HIPAA requirements",
    "Develop a DevOps transformation roadmap using AWS CodePipeline, CodeBuild, and CodeDeploy",
    "Write a database migration and optimization plan from Oracle to AWS RDS Aurora",
    "Generate a machine learning infrastructure proposal for real-time fraud detection using AWS services",
    "Create a containerization strategy document for migrating monolithic applications to AWS EKS",
    "Develop a data lake architecture specification using AWS S3, Glue, and Athena for analytics"
  ];

  const handleExampleClick = (examplePrompt: string) => {
    setPrompt(examplePrompt);
  };

  return (
    <div className="welcome-container">
      <div className="welcome-content">
        <div className="welcome-header">
          <h1>Minfy Proposal Builder</h1>
          <p>AI-powered solution document creator</p>
        </div>

        <div className="welcome-main">
          <div className="welcome-description">
            <h2>Create Professional Documents with AI</h2>
            <p>
              Describe your cloud transformation needs, and our AI Solution Architect will help you create 
              comprehensive, professional documents. From AWS migration strategies to AI/ML infrastructure 
              proposals, we cover all aspects of cloud services and digital transformation.
            </p>
          </div>

          <div className="prompt-section">
            <form onSubmit={handleSubmit} className="prompt-form">
              <div className="generation-options">
                <label className="deep-research-toggle">
                  <input
                    type="checkbox"
                    checked={useDeepResearch}
                    onChange={(e) => setUseDeepResearch(e.target.checked)}
                    disabled={isLoading}
                  />
                  <span className="toggle-label">
                    <span className="toggle-icon">🔍</span>
                    DeepResearch Mode
                  </span>
                  <span className="toggle-description">
                    {useDeepResearch 
                      ? "Uses advanced AI for comprehensive, multi-page documents with detailed research"
                      : "Uses structured generation for organized, section-by-section content"
                    }
                  </span>
                </label>
              </div>

              <div className="input-wrapper">
                <textarea
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Describe your cloud transformation needs, AWS migration requirements, or technical architecture needs..."
                  disabled={isLoading}
                  rows={4}
                  className="prompt-input"
                />
                
                <button
                  type="submit"
                  disabled={!prompt.trim() || isLoading}
                  className="start-button"
                >
                  {isLoading ? (
                    <div className="loading-spinner">
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                  ) : (
                    <>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M22 2L11 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M22 2L15 22L11 13L2 9L22 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      {useDeepResearch ? 'Start DeepResearch' : 'Start Creating'}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>

          <div className="examples-section">
            <h3>Try these examples:</h3>
            <div className="examples-grid">
              {examplePrompts.map((example, index) => (
                <button
                  key={index}
                  onClick={() => handleExampleClick(example)}
                  className="example-button"
                  disabled={isLoading}
                >
                  {example}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="welcome-features">
          <div className="feature">
            <div className="feature-icon">📝</div>
            <h4>Rich Text Editor</h4>
            <p>Edit and format your documents with our powerful Quill.js editor</p>
          </div>
          <div className="feature">
            <div className="feature-icon">🤖</div>
            <h4>AI Solution Architect</h4>
            <p>Expert guidance from our AI Solution Architect for cloud transformation</p>
          </div>
          <div className="feature">
            <div className="feature-icon">⚡</div>
            <h4>Real-time Generation</h4>
            <p>Watch your document being created in real-time with live progress tracking</p>
          </div>
          <div className="feature">
            <div className="feature-icon">🔍</div>
            <h4>DeepResearch Mode</h4>
            <p>Generate comprehensive, multi-page documents with advanced AI research</p>
          </div>
          <div className="feature">
            <div className="feature-icon">☁️</div>
            <h4>AWS Expertise</h4>
            <p>Specialized in AWS migration, modernization, and cloud-native solutions</p>
          </div>
          <div className="feature">
            <div className="feature-icon">🔧</div>
            <h4>Technical Excellence</h4>
            <p>Professional technical documentation for enterprise cloud solutions</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Welcome; 